import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { LoginSVG } from '../images';
import React, { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react'
import { Alert, Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/auth-context';
import { SignupCredentials } from '../types';
import { formatPhoneNumber } from '../helper/utils';


const Signup = () => {

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [isTogglePassword, setIsTogglePassword] = useState<boolean>(false);
    const [credentials, setCredentials] = useState<SignupCredentials>({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        password: ''
    });
    const { register, isAuth, errorMessage } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuth) {
            navigate('/customer/dashboard', {replace: true});
        }
    }, [isAuth])


    const handleValueChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target
        setCredentials((prev) => ({ ...prev, [name]: value.trim() }));
    }, [])

    const handleAuthentication = useCallback((event: FormEvent<HTMLFormElement>) => {
        const form = event.currentTarget;
        event.preventDefault();

        if (form.checkValidity() === false) {
            event.stopPropagation();
        }

        if (
            credentials.email && 
            credentials.password && 
            credentials.firstName && 
            credentials.lastName && 
            credentials.phone
        ) {
            setIsLoading(true);
            const payload = {...credentials, phone: formatPhoneNumber(credentials.phone)};
            register(payload);
            setIsLoading(false);
        }

        setValidated(true);
    }, [credentials])

    return (
        <section className="my-10">
			<Container>
				<Row className="d-flex justify-content-center align-items-start" style={{margin: '5em 0'}}>
                    <Col md={7} className='d-none d-sm-none d-xs-none d-md-none d-lg-flex justify-content-center'>
                        <img src={LoginSVG} alt="Login SVG" className="max-w-full h-auto" />
                    </Col>
                    <Col md={5} className="d-flex justify-content-start">
                        <Col md={8}>
                            {errorMessage && (<Alert className='py-2' style={{fontSize: '.9rem'}} variant='danger'>{errorMessage}</Alert>)}
                            <div className="flex flex-col">
                                <h1 style={{
                                    fontSize: '1.75rem',
                                    marginBottom: 0,
                                }}>Signup with EasySauces</h1>
                                <p style={{
                                    fontSize: '.8rem',
                                }}>Welcome back to Easysauces! Signup to get started.</p>
                            </div>
                            <Form validated={validated} noValidate onSubmit={handleAuthentication}>
                                <Row>
                                    <Col xs={12} md={12}>
                                        <Form.Group as={Col} md={12} className="mb-3" controlId="credentials.firstName">
                                            <Form.Control 
                                                required 
                                                type="text" 
                                                placeholder="First name" 
                                                className='border' 
                                                name='firstName'
                                                value={credentials.firstName} 
                                                onChange={handleValueChange}
                                            />
                                            <Form.Control.Feedback type='invalid'>First name is empty.</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={12}>
                                        <Form.Group as={Col} md={12} className="mb-3" controlId="credentials.lastName">
                                            <Form.Control 
                                                required 
                                                type="text" 
                                                placeholder="Last name" 
                                                className='border' 
                                                name='lastName'
                                                value={credentials.lastName} 
                                                onChange={handleValueChange}
                                            />
                                            <Form.Control.Feedback type='invalid'>Last name is empty.</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={12}>
                                        <Form.Group as={Col} md={12} className="mb-3" controlId="credentials.phone">
                                            <Form.Control 
                                                required 
                                                type="text" 
                                                placeholder="Phone" 
                                                className='border' 
                                                name='phone'
                                                value={credentials.phone} 
                                                onChange={handleValueChange}
                                            />
                                            <Form.Control.Feedback type='invalid'>Phone number is empty.</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={12}>
                                        <Form.Group as={Col} md={12} className="mb-3" controlId="credentials.email">
                                            <Form.Control 
                                                required 
                                                type="email" 
                                                placeholder="Email" 
                                                className='border' 
                                                name='email'
                                                value={credentials.email} 
                                                onChange={handleValueChange}
                                            />
                                            <Form.Control.Feedback type='invalid'>Invalid email address.</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={12} className="position-relative">
                                        <Form.Group as={Col} md={12} className="mb-3" controlId="credentials.password">
                                            <Form.Control 
                                                required 
                                                type={`${isTogglePassword ? "text" : "password"}`} 
                                                placeholder="******" 
                                                className='border' 
                                                style={{ width: '100%' }}
                                                name='password'
                                                value={credentials.password}
                                                onChange={handleValueChange}
                                            />
                                            <Form.Control.Feedback type='invalid'>Invalid password</Form.Control.Feedback>
                                            <span 
                                                className='position-absolute' 
                                                style={{
                                                    top: '3px',
                                                    right: '22px',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => setIsTogglePassword(!isTogglePassword)}>
                                                { isTogglePassword ? <EyeIcon width={20} /> : <EyeSlashIcon width={20} /> }
                                            </span>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <div>
                                    <Button 
                                        variant='danger' 
                                        type="submit" 
                                        className="btn items-center rounded"
                                        style={{
                                            width: '100%'
                                        }}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? <Spinner size='sm' variant='light' animation='border' /> : "Register"}
                                    </Button>
                                </div>
                                <div className="mt-2"
                                    style={{
                                        fontSize: '.8rem'
                                    }}
                                >
                                    Already have an account?&nbsp;
                                    <Link to="/login" className="text-danger">Sign In</Link>
                                </div>
                            </Form>
                        </Col>
                    </Col>
                </Row>
			</Container>
		</section>
    )
}

export default Signup
